import React, { useState, useEffect } from 'react';
import GlobalScamMap from '../components/GlobalScamMap';
import {
  aggregateAllScamData,
  calculateScamStats,
  filterScams,
  SCAM_TYPES,
  SCAM_SEVERITY
} from '../utils/scamDataAggregator';
import {
  Globe,
  Filter,
  TrendingUp,
  DollarSign,
  Users,
  AlertTriangle,
  MapPin,
  Activity,
  Shield,
  RefreshCw,
  Download
} from 'lucide-react';

const ScamMapPage = () => {
  const [scams, setScams] = useState([]);
  const [filteredScams, setFilteredScams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedScam, setSelectedScam] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    severity: 'all',
    verifiedOnly: false,
    startDate: '',
    endDate: '',
    minAmount: 0
  });

  // Load scam data
  useEffect(() => {
    loadScamData();
  }, []);

  // Apply filters when they change
  useEffect(() => {
    const filtered = filterScams(scams, filters);
    setFilteredScams(filtered);
    setStats(calculateScamStats(filtered));
  }, [scams, filters]);

  const loadScamData = async () => {
    setLoading(true);
    try {
      const data = await aggregateAllScamData();
      setScams(data);
      setFilteredScams(data);
      setStats(calculateScamStats(data));
    } catch (error) {
      console.error('Error loading scam data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      type: 'all',
      severity: 'all',
      verifiedOnly: false,
      startDate: '',
      endDate: '',
      minAmount: 0
    });
  };

  const exportData = () => {
    const dataStr = JSON.stringify(filteredScams, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scam-data-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(2)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading global scam data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      {/* Header */}
      <div className="glass-card rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Global Scam Map</h1>
              <p className="text-gray-400">Live tracking of cryptocurrency fraud worldwide</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadScamData}
              className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/50 rounded-lg text-cyan-400 transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={exportData}
              className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-purple-400 transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Statistics Dashboard */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                <span className="text-xs text-gray-400">Total Reports</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.total.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">
                {stats.recentScams} in last 30 days
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-amber-400" />
                <span className="text-xs text-gray-400">Total Loss</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {formatCurrency(stats.totalAmount)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Avg: {formatCurrency(stats.averageAmount)}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-xs text-gray-400">Total Victims</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {stats.totalVictims.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Worldwide impact
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-xs text-gray-400">Critical</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {stats.bySeverity[SCAM_SEVERITY.CRITICAL] || 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                High priority cases
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </h2>
              <button
                onClick={resetFilters}
                className="text-xs text-gray-400 hover:text-cyan-400 transition-colors"
              >
                Reset
              </button>
            </div>

            <div className="space-y-4">
              {/* Scam Type Filter */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Scam Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 focus:border-cyan-500/50 transition-colors"
                >
                  <option value="all">All Types</option>
                  {Object.values(SCAM_TYPES).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Severity Filter */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Severity</label>
                <select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 focus:border-cyan-500/50 transition-colors"
                >
                  <option value="all">All Levels</option>
                  <option value={SCAM_SEVERITY.LOW}>Low</option>
                  <option value={SCAM_SEVERITY.MEDIUM}>Medium</option>
                  <option value={SCAM_SEVERITY.HIGH}>High</option>
                  <option value={SCAM_SEVERITY.CRITICAL}>Critical</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Date From</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 focus:border-cyan-500/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Date To</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 focus:border-cyan-500/50 transition-colors"
                />
              </div>

              {/* Minimum Amount */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Min Amount ($)</label>
                <input
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 focus:border-cyan-500/50 transition-colors"
                  placeholder="0"
                />
              </div>

              {/* Verified Only */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="verified"
                  checked={filters.verifiedOnly}
                  onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                  className="w-4 h-4 rounded bg-white/5 border-white/10 text-cyan-500 focus:ring-cyan-500"
                />
                <label htmlFor="verified" className="text-sm text-gray-300 flex items-center gap-1">
                  <Shield className="w-4 h-4 text-green-400" />
                  Verified Only
                </label>
              </div>

              {/* Heatmap Toggle */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                <input
                  type="checkbox"
                  id="heatmap"
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                  className="w-4 h-4 rounded bg-white/5 border-white/10 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="heatmap" className="text-sm text-gray-300 flex items-center gap-1">
                  <Activity className="w-4 h-4 text-purple-400" />
                  Show Heatmap
                </label>
              </div>
            </div>
          </div>

          {/* Top Scam Types */}
          {stats && (
            <div className="glass-card rounded-xl p-4">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Top Scam Types
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.byType)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{type}</span>
                      <span className="text-cyan-400 font-medium">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Map Container */}
        <div className="lg:col-span-3">
          <div className="glass-card rounded-xl overflow-hidden" style={{ height: '70vh' }}>
            <GlobalScamMap
              scams={filteredScams}
              selectedScam={selectedScam}
              onScamSelect={setSelectedScam}
              showHeatmap={showHeatmap}
            />
          </div>

          {/* Map Info */}
          <div className="mt-4 glass-card rounded-xl p-4">
            <p className="text-sm text-gray-400">
              Showing <span className="text-cyan-400 font-bold">{filteredScams.length}</span> scam
              reports out of <span className="text-white font-bold">{scams.length}</span> total.
              Click on markers for details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamMapPage;
